<template>
  <div class="report-filter-dropdowns">
    <div
      v-for="filterField in reportFilters"
      :key="filterField"
      class="filter-dropdown-container q-ma-xs"
    >
      <q-select
        v-model="selectedValues[filterField]"
        :options="filterOptions[filterField] || []"
        :label="getFilterLabel(filterField)"
        :loading="loadingFilters[filterField]"
        multiple
        use-chips
        use-counter
        clearable
        style="min-width: 200px; max-width: 300px;"
        @update:model-value="handleFilterChange(filterField, $event)"
        @focus="loadFilterOptionsOnOpening(filterField)"
      >
        <template #option="scope">
          <q-item v-bind="scope.itemProps">
            <q-item-section>
              <q-item-label>{{ scope.opt.label || scope.opt }}</q-item-label>
            </q-item-section>
          </q-item>
        </template>
      </q-select>
    </div>
  </div>
</template>

<script>
import Report, { SYSTEM_REPORTS } from 'src/models/report/Report'

export default {
  name: 'ReportFilterDropdowns',
  props: {
    report: {
      type: Object,
      required: true
    },
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['filters-changed', 'update:model-value'],
  data () {
    return {
      filterOptions: {},
      selectedValues: {},
      loadingFilters: {},
      initialized: false,
      shown: false,
      initializedFilters: new Set()
    }
  },
  computed: {
    reportFilters () {
      const filters = this.report.defaultOptions?.report_filters || []
      return typeof filters === 'string'
        ? filters.split(',').map(item => item.trim()).filter(item => item.length > 0)
        : filters
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    },
    'report.defaultOptions.report_filters': {
      immediate: true,
      deep: true,
      handler (newFilters, oldFilters) {
        if (newFilters && newFilters.length > 0) {
          this.initializeFilters()
        }

        if (oldFilters !== undefined) {
          let oldFiltersArray = oldFilters
          if (typeof oldFilters === 'string') {
            oldFiltersArray = oldFilters.split(',').map(item => item.trim()).filter(item => item.length > 0)
          } else if (!Array.isArray(oldFilters)) {
            oldFiltersArray = []
          }

          if (oldFiltersArray && oldFiltersArray.length > 0) {
            const removedFilters = oldFiltersArray.filter(filter => !newFilters.includes(filter))
            removedFilters.forEach(filterField => {
              this.initializedFilters.delete(filterField)
              delete this.filterOptions[filterField]
              delete this.selectedValues[filterField]
              delete this.loadingFilters[filterField]
            })
          }
        }
      }
    },
    'report.elements.data': {
      immediate: true,
      handler () {
        if (this.reportFilters && this.reportFilters.length > 0 && this.report.elements?.data?.length > 0) {
          this.initializeFilters()
        }
      }
    },
    'report.initialOptions': {
      immediate: true,
      handler () {
        if (this.reportFilters && this.reportFilters.length > 0) {
          this.initializeFilters()
        }
      },
      deep: true
    },
    filterOptions: {
      handler () {
        this.reportFilters.forEach(filterField => {
          if (this.filterOptions[filterField] && this.filterOptions[filterField].length > 0) {
            const filterKey = `${filterField}_filter`
            const storedValues = this.report.defaultOptions[filterKey] || []
            this.selectedValues[filterField] = storedValues.map(value => {
              if (typeof value === 'object' && value.label !== undefined) {
                return value
              }
              const option = this.filterOptions[filterField]?.find(opt =>
                opt.value === value || opt.label === value
              )
              return option || { label: value, value }
            })
          }
        })
      },
      deep: true
    },
    reportFilters: {
      immediate: true,
      handler (newFilters) {
        if (newFilters && newFilters.length > 0) {
          this.initializeFilters()
        }
      }
    },
    'report.defaultOptions': {
      deep: true,
      handler (newOptions) {
        if (newOptions?.report_filters && newOptions.report_filters.length > 0) {
          this.initializeFilters()
        }
      }
    }
  },
  configuration: ['currentCircuit'],
  methods: {
    show () {
      this.shown = true
    },
    hide () {
      this.shown = false
    },
    async initializeFilters () {
      if (!this.report.elements?.data?.length) return

      if (!this.report.initialOptions && Object.keys(this.report.defaultOptions || {}).length === 0) {
        return
      }

      this.initialized = true

      this.reportFilters.forEach(filterField => {
        if (!this.initializedFilters.has(filterField)) {
          this.initializedFilters.add(filterField)

          if (!this.filterOptions[filterField]) {
            this.filterOptions[filterField] = []
          }
          this.loadFilterOptions(filterField)
        }
      })
    },

    getFilterLabel (filterField) {
      const column = this.report.filterableColumns?.find(col => col.field === filterField)
      return column?.label || _.startCase(filterField)
    },

    async loadFilterOptions (filterField, loadAll = true) {
      if (this.loadingFilters[filterField]) {
        return
      }

      this.loadingFilters[filterField] = true

      try {
        let reportElement = this.report.elements?.data?.find(element => {
          return element.filterableColumns?.some(col => col.field === filterField)
        })
        if (!reportElement && this.report.elements?.data?.length > 0) {
          reportElement = this.report.elements.data[0]
        }

        if (!reportElement) {
          this.filterOptions[filterField] = []
          this.loadingFilters[filterField] = false
          return
        }

        const reportOptions = {
          ...reportElement.finalOptions,
          group_by: [filterField]
        }
        reportElement.resetAllFilters(reportOptions)

        // For performance reasons, only load the first day of data
        if (!loadAll) {
          reportOptions.end_date = reportOptions.start_date
        }

        const optionsLoaderReport = new Report({
          id: this.currentCircuit.reportIdForName(SYSTEM_REPORTS.OPTIONS_LOADER_REPORT),
          reportOptions
        }, {
          reportStatus: true,
          elements: {
            possibleOptionsValues: true
          }
        })

        await optionsLoaderReport.runReport()

        this.checkReportStatus(optionsLoaderReport, filterField)
      } catch (error) {
        this.filterOptions[filterField] = []
        this.loadingFilters[filterField] = false
      }
    },

    checkReportStatus (optionsLoaderReport, filterField) {
      if (!optionsLoaderReport.reportStatus.done && !optionsLoaderReport.reportStatus.error) {
        setTimeout(() => {
          this.checkReportStatus(optionsLoaderReport, filterField)
        }, 100)
        return
      }

      if (optionsLoaderReport.reportStatus.done) {
        const options = optionsLoaderReport.elements.data[0]?.possibleOptionsValues[`${filterField}_filter`] || []
        this.filterOptions[filterField] = options
          .map(option => {
            let label = ''
            let value = ''

            if (typeof option === 'string') {
              label = option
              value = option
            } else if (typeof option === 'object' && option !== null) {
              label = option.label || option.name || option.display || ''
              value = option.value || option.id || option.key || ''
            }

            if (label && value) {
              return { label, value }
            }
            return null
          })
          .filter(option => option !== null)
      } else if (optionsLoaderReport.reportStatus.error) {
        this.filterOptions[filterField] = []
      }

      this.loadingFilters[filterField] = false
      optionsLoaderReport.destroy()
      optionsLoaderReport = null
    },

    handleFilterChange (filterField, values) {
      const filterKey = `${filterField}_filter`
      const excludeKey = `exclude_${filterField}`

      const extractedValues = values
        ? _.map(values, item => {
          if (typeof item === 'object' && item.value !== undefined) {
            return item.value
          }
          return item
        })
        : []

      this.report.reportOptions[filterKey] = extractedValues
      this.report.reportOptions[excludeKey] = false
      this.report.reportOptions = { ...this.report.reportOptions }

      this.$emit('filters-changed')
    },

    loadFilterOptionsOnOpening (filterField) {
      this.loadFilterOptions(filterField)
    }
  }
}
</script>

<style lang="scss" scoped>
.report-filter-dropdowns {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.filter-dropdown-container {
  flex-shrink: 0;
}
</style>
